import constants from "../constants";
import { get, getQueryString, getQueryNonEmptyString, post } from "../utils";
import {
  InventoryFilterParamObj,
  InventoryTableRequestParams,
  SalesTableRequestParams,
  TransactionTableRequestParams
} from "src/pages/ItemDetails/interface";
import { BasicDetailsArgs } from "src/pages/ItemDetails/slice/basicDetails.slice";
import {
  NoSaleRequestParams,
  DeptExceptionRequestParams,
  TransferAcitivityRequestParams,
  PriceChangesDataRequestParams,
  ProductsRequestParams
} from "src/config/tableInterface/interface";
import {
  RequestParams,
  TopItemsFilterParams
} from "src/pages/RealTimeSalesReport/interface";
import { FilterParamObj } from "src/pages/Dashboard/interface";
import { getFormattedDateFromDifferentFormate } from "../../utils/getDates";
import {
  DependentDropDownPayloadRequest,
  SearchQueryPayload,
  TransactionDetailsQuery
} from "src/pages/TransactionLogReport/interface";

export const createQueryString = <T>(paramsObj: T) => {
  let qs = "?";
  for (const key in paramsObj) {
    qs += `${key}=${paramsObj[key]}&`;
  }

  return qs.slice(0, qs.length - 1);
};

export const getCompSalesData = ({
  storeId,
  fromDate,
  toDate
}: {
  storeId: string;
  fromDate: string;
  toDate: string;
}) => {
  const queryString = getQueryString({
    storeId,
    fromDate,
    toDate
  });

  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/bi/reports/compSales" +
    `?${queryString}`;

  return get(URL);
};

export const getAncptMarginData = ({
  storeId,
  fromDate,
  toDate
}: {
  storeId: string;
  fromDate: string;
  toDate: string;
}) => {
  const queryString = getQueryString({
    storeId,
    fromDate,
    toDate
  });

  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/bi/reports/anticipatedMargin" +
    `?${queryString}`;

  return get(URL);
};

export const getTopWPSAData = ({
  department,
  storeFinId,
  page
}: {
  department: string;
  storeFinId: string;
  page: number;
}) => {
  const queryObj = {
    department,
    storeFinId,
    pageIndex: page
  };

  if (queryObj.department === "All") delete queryObj.department;

  const queryString = getQueryString(queryObj);

  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/bi/reports/topWPSAChange?${queryString}`;
  return get(URL);
};

export const getRecentIcareData = ({ storeId }: { storeId: string }) => {
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/icare_alerts/store_tickets?storeId=${storeId}`;
  return get(URL, true);
};

export const getProductsListData = (payload: ProductsRequestParams) => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/dw/products/list";
  return post(URL, payload, true, false, false);
};

export const getProductListTags = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/dw/products/tags";
  return get(URL, true);
};

export const getUserData = () => {
  const URL =
    constants.SERVICES_API_BASE_URL + "v1/bi/reports/userdetails?storeId=333";
  return get(URL);
};

export const getMasterList = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/dw/merchHierarchyList";
  return get(URL);
};

export const getFilterList = (filterParamObj: FilterParamObj) => {
  const queryString = getQueryString(filterParamObj);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/dw/products/filters" +
    `?${queryString}`;
  return get(URL);
};

export const getNoSalesFilterList = (filterParamObj: FilterParamObj) => {
  const queryString = getQueryString(filterParamObj);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/dw/products/noSales/filters" +
    `?${queryString}`;
  return get(URL);
};

export const getyInventorFilterList = (
  filterParamObj: InventoryFilterParamObj
) => {
  const queryString = getQueryString(filterParamObj);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/dw/products/inventory/filters" +
    `?${queryString}`;
  return get(URL);
};

export const getProductsForMT = (
  searchQuery: string,
  storeId: string,
  recevingStoreId: string,
  signal: AbortSignal
) => {
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/dw/inventories/store/search?storeId=${storeId}&recevingStoreId=${recevingStoreId}&pageIndex=1&query=${encodeURI(
      searchQuery
    )}&pageSize=50`;

  return get(URL, true, false, false, signal);
};

export const getProductsForOS = ({
  query,
  storeId,
  wh,
  pageIndex = 1,
  pageSize = 50,
  sortParam = "",
  sortOrder = "DESC",
  signal
}: {
  query: string;
  storeId: string;
  wh: string;
  pageIndex?: number;
  pageSize?: number;
  sortParam?: string;
  sortOrder?: string;
  signal: AbortSignal;
}) => {
  const queryString = getQueryNonEmptyString({
    query,
    storeId,
    sortOrder,
    pageIndex,
    wh,
    pageSize,
    sortParam
  });
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/dw/inventories/warehouse/search?${queryString}`;

  return get(URL, true, false, false, signal);
};

export const getStoreListForMT = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/goapps/storesList";

  return get(URL, true);
};

export const getItemBasicDetails = ({ storeId, itemId }: BasicDetailsArgs) => {
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v2/dw/products/details" +
    `?storeId=${storeId}&itemId=${itemId}`;

  return get(URL);
};

export const getItemRetail = ({ storeId, itemId }: BasicDetailsArgs) => {
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/dw/products/itemRetail" +
    `?storeId=${storeId}&itemId=${itemId}`;

  return get(URL);
};

export const getTransactionFilters = ({
  storeId,
  itemId,
  fromDate,
  toDate
}: {
  storeId: string;
  itemId: string;
  fromDate: string;
  toDate: string;
}) => {
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/dw/products/transaction/filters" +
    `?storeId=${storeId}&itemId=${itemId}&fromDate=${fromDate}&toDate=${toDate}`;

  return get(URL);
};

export const getInventoryTableData = (
  paramsObj: InventoryTableRequestParams
) => {
  const payload = { ...paramsObj };
  delete payload.isRandomWeightItem;
  const queryString = createQueryString(payload);
  const URL =
    constants.SERVICES_API_BASE_URL + "v2/dw/products/inventory" + queryString;

  return get(URL);
};

export const getSalesTableData = (paramsObj: SalesTableRequestParams) => {
  const queryString = createQueryString(paramsObj);
  const URL =
    constants.SERVICES_API_BASE_URL + "v1/dw/products/sales" + queryString;

  return get(URL);
};

export const getTransactionTableData = (
  paramsObj: TransactionTableRequestParams
) => {
  const queryString = createQueryString(paramsObj);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/dw/products/transaction" +
    queryString;

  return get(URL);
};

export const getDeptExceptionData = (payload: DeptExceptionRequestParams) => {
  const URL =
    constants.SERVICES_API_BASE_URL + "v1/dw/products/departmentException";
  return post(URL, payload, true, false, false);
};

export const getPriceChangesViewAll = (paramObj: any) => {
  const { signal } = paramObj;
  delete paramObj.signal;
  paramObj.fromDate = getFormattedDateFromDifferentFormate(
    paramObj.fromDate,
    "MM/DD/YY",
    "MM/DD/YYYY"
  );
  paramObj.toDate = getFormattedDateFromDifferentFormate(
    paramObj.toDate,
    "MM/DD/YY",
    "MM/DD/YYYY"
  );
  const queryString = getQueryNonEmptyString(paramObj);
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/bbic/products/priceChangeList?${queryString}`;
  return get(URL, true, false, false, signal);
};

export const getBachList = ({ storeId }: { storeId: string | null }) => {
  const queryString = getQueryNonEmptyString({ storeId });
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/bbic/products/priceChangeBatchList?${queryString}`;
  return get(URL);
};

export const getPriceChangesHistory = ({
  itemId,
  storeId
}: {
  itemId: string;
  storeId: string;
}) => {
  const queryString = getQueryNonEmptyString({
    itemId,
    storeId
  });
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/dw/products/priceChangeHistory?${queryString}`;
  return get(URL);
};

export const getNoSaleData = (payload: NoSaleRequestParams) => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/dw/products/noSales";
  return post(URL, payload, true, false, false);
};

export const getNoSalesRecordsCount = (payload: NoSaleRequestParams) => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/dw/products/noSales/count";
  return post(URL, payload, true, false, false);
};

export const getCreditTable = (props: any) => {
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/bi/creditPO/list?docId=${props.docId}&pageIndex=${props.pageIndex}&pageSize=${props.pageSize}&sortOrder=${props.sortOrder}&sortParam=${props.sortParam}`;
  return get(URL);
};

export const getSalesReportData = (props: any) => {
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/dw/reports/salesInquiry?storeId=${props.storeId}&date=${props.date}`;
  return get(URL);
};

export const getTransferActivityData = (
  payload: TransferAcitivityRequestParams
) => {
  const { signal } = payload;
  delete payload.signal;
  const queryString = createQueryString(payload);
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/dw/reports/transferActivityRegister${queryString}`;
  return get(URL, true, false, false, signal);
};

export const getFlashStoreData = (props: any) => {
  const queryString = getQueryNonEmptyString(props);
  const URL =
    constants.SERVICES_API_BASE_URL + `v1/bi/reports/flashSales?${queryString}`;
  return get(URL);
};
export const getStoreGroupMultiSelectFilter = () => {
  const URL =
    constants.SERVICES_API_BASE_URL + "v1/bi/reports/regionsAndStoreTypes";
  return get(URL);
};
export const getFlashHierarchyFilterData = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/bi/reports/dsmHierarchy";
  return get(URL);
};

export const getPriceChangesData = (
  paramObj: PriceChangesDataRequestParams
) => {
  const { signal } = paramObj;
  delete paramObj.signal;
  const queryString = createQueryString(paramObj);
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/bbic/reports/priceChangeMarkupsAndMarkdown${queryString}`;
  return get(URL, true, false, false, signal);
};

export const fetchDepartmentSales = async (
  params: RequestParams & { signal: AbortSignal }
) => {
  const { signal } = params;
  delete params.signal;
  const queryString = createQueryString(params);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/bi/reports/realTimeDepartmentSales" +
    queryString;

  return get(URL, true, false, false, signal);
};

export const fetchHourlySales = async (
  params: RequestParams & { signal: AbortSignal }
) => {
  const { signal } = params;
  delete params.signal;
  const queryString = createQueryString(params);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/bi/reports/realTimeHourlySales" +
    queryString;

  return get(URL, true, false, false, signal);
};

export const fetchStoreListData = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/dw/storeListMaster";

  return get(URL);
};

export const fetchTopItems = (
  params: TopItemsFilterParams & { signal: AbortSignal }
) => {
  const { signal } = params;
  delete params.signal;
  const queryString = createQueryString(params);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/bi/reports/realTimeTopSalesItems" +
    queryString;

  return get(URL, true, false, false, signal);
};

export const fetchStoreGroupData = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/bi/reports/storeGroup";

  return get(URL);
};

/* istanbul ignore next */
export const updateWishlistStatus = (params: any) => {
  const queryString = createQueryString({
    ...params,
    as400order: 0
  });

  const URL =
    constants.SERVICES_API_BASE_URL +
    "v1/goapps/products/itemWishlist" +
    queryString;

  return get(URL);
};

export const getTransactionDetails = (params: {
  storeId: string;
  transactionId: string;
  time: string;
  registerNumber: string;
  date: string;
}) => {
  const queryString = createQueryString(params);
  const URL =
    constants.SERVICES_API_BASE_URL +
    "v2/dw/products/transaction/detail" +
    queryString;

  return get(URL);
};

export const getDSMStoreMapping = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/bi/reports/DSMStoreList";
  return get(URL);
};

export const getTransactionsByFitler = (payload: SearchQueryPayload) => {
  const { signal } = payload;
  delete payload.signal;
  const URL = `${constants.SERVICES_API_BASE_URL}v1/dw/reports/transactionLogSearch`;
  return post(URL, payload, true, false, false, signal);
};

export const getTlogReportDependentDropdowns = (
  params: DependentDropDownPayloadRequest
) => {
  const queryString = createQueryString(params);
  const URL = `${constants.SERVICES_API_BASE_URL}v1/dw/reports/transactionFilters${queryString}`;
  return get(URL);
};

export const getTlogReportTransactionDetail = (
  params: TransactionDetailsQuery
) => {
  const queryString = createQueryString(params);
  const URL = `${constants.SERVICES_API_BASE_URL}v2/dw/products/transaction/detail${queryString}`;
  return get(URL);
};

export const getWarehouseList = () => {
  const URL = constants.SERVICES_API_BASE_URL + "v1/goapps/warehouseList";
  return get(URL);
};

export const getProductsForJITOrder = (
  searchQuery: string,
  storeId: string,
  signal: AbortSignal
) => {
  return getProductsForMT(searchQuery, storeId, "", signal);
};

export const getDiscrepancyList = ({
  storeId,
  orderDate,
  departmentId,
  signal
}: {
  storeId: string;
  orderDate: string;
  departmentId: string;
  signal?: AbortSignal;
}) => {
  const queryString = getQueryString({ storeId, orderDate, departmentId });
  const URL =
    constants.SERVICES_API_BASE_URL +
    `v1/dw/order/pmrjitDiscrepancyReport?${queryString}`;

  return get(URL, true, false, false, signal);
};
